import { FlatpickrDefaultsInterface } from 'angularx-flatpickr';
import flatpickr from 'flatpickr';
import { DayElement } from 'flatpickr/dist/types/instance';
import { Plugin } from 'flatpickr/dist/types/options';
import { Subject } from 'rxjs';

const ANO_MINIMO = 1900;
const ANO_MAXIMO = 2100;

/** Formatos compostos apenas por dia, mês, ano e hora numéricos, com separadores. */
const FORMATO_NUMERICO = /^[djmnYHGiSs\s/:.\-]+$/;

const TOKENS_NUMERICOS: Record<string, string> = {
  d: '(\\d{1,2})',
  j: '(\\d{1,2})',
  m: '(\\d{1,2})',
  n: '(\\d{1,2})',
  Y: '(\\d{4})',
  H: '(\\d{1,2})',
  G: '(\\d{1,2})',
  i: '(\\d{1,2})',
  S: '(\\d{1,2})',
  s: '(\\d{1,2})'
};

const TOKENS_HORA = ['H', 'G', 'i', 'S', 's'];

export interface AvisoData {
  titulo: string;
  mensagem: string;
}

/**
 * Emite os avisos sobre datas informadas pelo usuário (data inválida ou fora do período permitido).
 */
export const avisoDataDigitada$ = new Subject<AvisoData>();

/**
 * Parser de datas do flatpickr que não converte valores inválidos em outra data.
 *
 * Nos formatos de exibição (com "/", como d/m/Y e d/m/Y H:i), o texto precisa corresponder
 * ao formato inteiro (ou somente à parte de data), a data precisa existir no calendário e o
 * ano deve estar entre 1900 e 2100. Assim, "31/09/2026" e "10/10/0416" são rejeitados em vez
 * de virarem outra data, e o usuário é avisado. Os demais formatos (valores do modelo, como
 * Y-m-d) continuam com o parser padrão do flatpickr.
 */
export function parseDataEstrita(texto: string, formato: string): Date | undefined {
  const data = interpretarData(texto, formato);

  if (!data && ehFormatoExibicao(formato)) {
    avisoDataDigitada$.next({
      titulo: 'Data inválida',
      mensagem: `A data "${texto}" não é válida. Informe uma data existente no formato dd/mm/aaaa, com ano entre 1900 e 2100.`
    });
  }

  return data;
}

/**
 * Interpreta a data com as mesmas regras do parseDataEstrita, sem avisar o usuário.
 */
export function interpretarData(texto: string, formato: string): Date | undefined {
  if (!ehFormatoExibicao(formato)) {
    return parseDataPadrao(texto, formato);
  }

  return converterTexto(texto.trim(), formato) ?? converterTexto(texto.trim(), formatoSemHora(formato));
}

function ehFormatoExibicao(formato: string): boolean {
  return !!formato?.includes('/') && FORMATO_NUMERICO.test(formato);
}

/**
 * Plugin do flatpickr que aplica a máscara do formato no campo em que o usuário digita:
 * - aceita apenas dígitos e os separadores do formato; textos com letras são ignorados;
 * - limita a quantidade de dígitos ao formato (8 em dd/mm/aaaa, 12 em dd/mm/aaaa hh:mm);
 * - insere os separadores automaticamente ao digitar ou colar somente os números (28092026 → 28/09/2026);
 * - ao digitar um separador após um grupo incompleto, completa o grupo com zero (2/9 → 02/09), exceto o ano.
 * Formatos com nomes (ex.: "F Y") não recebem máscara.
 */
export const mascaraDataPlugin: Plugin = fp => ({
  onReady: () => {
    const formato = fp.config.altInput ? fp.config.altFormat : fp.config.dateFormat;

    if (fp.config.inline || !FORMATO_NUMERICO.test(formato)) {
      return;
    }

    const partes = partesMascara(formato);
    const separadores = formato.replace(/[djmnYHGiSs]/g, '');
    const maximoDigitos = partes.reduce((total, parte) => total + (parte.digitos ?? 0), 0);

    fp._bind(fp._input, 'beforeinput', (evento: InputEvent) => {
      const texto = evento.data ?? evento.dataTransfer?.getData('text/plain') ?? '';

      if (!evento.inputType?.startsWith('insert') || evento.inputType === 'insertCompositionText' || !texto) {
        return;
      }

      evento.preventDefault();

      if ([...texto].some(caractere => !/\d/.test(caractere) && !separadores.includes(caractere))) {
        return;
      }

      const input = fp._input;
      const valor = input.value;
      const inicio = input.selectionStart ?? valor.length;
      const fim = input.selectionEnd ?? valor.length;
      let novoValor: string;
      let cursor: number;

      if (fim === valor.length) {
        // Digitação ou colagem no fim do campo: processa caractere a caractere, como uma digitação
        let digitos = somenteDigitos(valor.slice(0, inicio));
        let terminaEmSeparador = false;

        for (const caractere of texto) {
          terminaEmSeparador = !/\d/.test(caractere);

          if (terminaEmSeparador) {
            digitos = completarGrupo(digitos, partes);
          } else if (digitos.length < maximoDigitos) {
            digitos += caractere;
          }
        }

        novoValor = formatarDigitos(digitos, partes, terminaEmSeparador);
        cursor = novoValor.length;
      } else {
        // Inserção no meio do texto: mantém a sequência de dígitos e reposiciona os separadores
        const digitosAntes = somenteDigitos(valor.slice(0, inicio) + texto);
        const digitos = (digitosAntes + somenteDigitos(valor.slice(fim))).slice(0, maximoDigitos);

        novoValor = formatarDigitos(digitos, partes);
        cursor = posicaoAposDigitos(novoValor, Math.min(digitosAntes.length, maximoDigitos));
      }

      input.value = novoValor;
      input.setSelectionRange(cursor, cursor);
    });
  }
});

/**
 * Plugin do flatpickr que avisa o usuário quando ele tenta informar uma data fora do período
 * permitido pelo campo (minDate/maxDate, ex.: data fim anterior à data início), seja digitando,
 * seja clicando em um dia bloqueado do calendário. A data continua sendo recusada pelo flatpickr.
 */
export const avisoPeriodoDataPlugin: Plugin = fp => ({
  onReady: () => {
    if (fp.config.inline) {
      return;
    }

    const formato = fp.config.altInput ? fp.config.altFormat : fp.config.dateFormat;
    const formatoLimite = fp.config.noCalendar ? 'H:i' : fp.config.enableTime ? 'd/m/Y H:i' : 'd/m/Y';

    const avisarSeForaDoPeriodo = (data: Date, somenteDia: boolean) => {
      const { minDate, maxDate } = fp.config;
      const antesDoMinimo = minDate && flatpickr.compareDates(data, minDate, somenteDia) < 0;
      const depoisDoMaximo = maxDate && flatpickr.compareDates(data, maxDate, somenteDia) > 0;

      if (antesDoMinimo || depoisDoMaximo) {
        const limite = antesDoMinimo ? `anterior a ${fp.formatDate(minDate, formatoLimite)}` : `posterior a ${fp.formatDate(maxDate, formatoLimite)}`;
        avisoDataDigitada$.next({ titulo: 'Data fora do período', mensagem: `A data informada não pode ser ${limite}.` });
      }
    };

    // Registrado na captura para rodar antes do flatpickr, que limpa a data recusada sem avisar
    const verificarTextoDigitado = (destino?: EventTarget) => {
      const texto = fp._input.value.trim();
      const [dataSelecionada] = fp.selectedDates;

      if (!texto || fp.calendarContainer?.contains(destino as Node) || (dataSelecionada && fp.formatDate(dataSelecionada, formato) === texto)) {
        return;
      }

      const data = interpretarData(texto, formato);

      if (data && !fp.isEnabled(data, false)) {
        avisarSeForaDoPeriodo(data, false);
      }
    };

    fp._bind(fp._input, 'blur', (evento: FocusEvent) => verificarTextoDigitado(evento.relatedTarget), { capture: true });
    fp._bind(
      fp._input,
      'keydown',
      (evento: KeyboardEvent) => {
        if (evento.key === 'Enter') {
          verificarTextoDigitado();
        }
      },
      { capture: true }
    );

    if (fp.calendarContainer) {
      fp._bind(fp.calendarContainer, 'click', (evento: MouseEvent) => {
        const dia = (evento.target as HTMLElement).closest?.('.flatpickr-day.flatpickr-disabled') as DayElement;

        if (dia?.dateObj) {
          avisarSeForaDoPeriodo(dia.dateObj, true);
        }
      });
    }
  }
});

/**
 * Opções padrão dos campos mwlFlatpickr: digitação manual com máscara, validação estrita da data
 * e aviso de data fora do período.
 */
export const FLATPICKR_OPCOES_PADRAO: FlatpickrDefaultsInterface = {
  allowInput: true,
  parseDate: parseDataEstrita as FlatpickrDefaultsInterface['parseDate'],
  plugins: [mascaraDataPlugin, avisoPeriodoDataPlugin]
};

interface ParteMascara {
  token?: string;
  digitos?: number;
  separador?: string;
}

function partesMascara(formato: string): ParteMascara[] {
  return formato.split('').reduce((partes, caractere) => {
    const ultima = partes[partes.length - 1];

    if (TOKENS_NUMERICOS[caractere]) {
      partes.push({ token: caractere, digitos: caractere === 'Y' ? 4 : 2 });
    } else if (ultima?.separador !== undefined) {
      ultima.separador += caractere;
    } else {
      partes.push({ separador: caractere });
    }

    return partes;
  }, [] as ParteMascara[]);
}

/**
 * Monta o texto a partir dos dígitos. O separador só aparece quando há dígito depois dele,
 * ou no fim, quando o usuário digitou o separador após completar o grupo.
 */
function formatarDigitos(digitos: string, partes: ParteMascara[], incluirSeparadorFinal = false): string {
  let texto = '';
  let usados = 0;

  for (const parte of partes) {
    if (parte.separador !== undefined) {
      const grupoCompleto = usados === digitos.length && usados > 0;

      if (usados < digitos.length || (incluirSeparadorFinal && grupoCompleto)) {
        texto += parte.separador;
        continue;
      }

      break;
    }

    if (usados >= digitos.length) {
      break;
    }

    texto += digitos.substr(usados, parte.digitos);
    usados += parte.digitos;
  }

  return texto;
}

/**
 * Completa com zero à esquerda o grupo que está sendo digitado (ex.: dia "2" → "02"). O ano não é completado.
 */
function completarGrupo(digitos: string, partes: ParteMascara[]): string {
  let inicio = 0;

  for (const parte of partes) {
    if (!parte.token) {
      continue;
    }

    const fim = inicio + parte.digitos;

    if (digitos.length < fim) {
      const digitadosNoGrupo = digitos.length - inicio;

      return digitadosNoGrupo > 0 && parte.token !== 'Y'
        ? digitos.slice(0, inicio) + digitos.slice(inicio).padStart(parte.digitos, '0')
        : digitos;
    }

    inicio = fim;
  }

  return digitos;
}

function somenteDigitos(texto: string): string {
  return texto.replace(/\D/g, '');
}

function posicaoAposDigitos(texto: string, quantidade: number): number {
  let encontrados = 0;

  for (let indice = 0; indice < texto.length && quantidade > 0; indice++) {
    if (/\d/.test(texto[indice]) && ++encontrados === quantidade) {
      return indice + 1;
    }
  }

  return quantidade > 0 ? texto.length : 0;
}

function converterTexto(texto: string, formato: string): Date | undefined {
  if (!formato) {
    return undefined;
  }

  const tokens: string[] = [];
  const padrao = formato
    .split('')
    .map(caractere => {
      if (TOKENS_NUMERICOS[caractere]) {
        tokens.push(caractere);
        return TOKENS_NUMERICOS[caractere];
      }

      return caractere === ' ' ? '\\s+' : caractere.replace(/[.\-/:]/g, '\\$&');
    })
    .join('');

  const partes = new RegExp(`^${padrao}$`).exec(texto);
  if (!partes) {
    return undefined;
  }

  const valores: Record<string, number> = {};
  tokens.forEach((token, indice) => (valores[token] = Number(partes[indice + 1])));

  const dia = valores.d ?? valores.j;
  const mes = valores.m ?? valores.n;
  const ano = valores.Y;
  const hora = valores.H ?? valores.G ?? 0;
  const minuto = valores.i ?? 0;
  const segundo = valores.S ?? valores.s ?? 0;

  if (ano < ANO_MINIMO || ano > ANO_MAXIMO || hora > 23 || minuto > 59 || segundo > 59) {
    return undefined;
  }

  const data = new Date(ano, mes - 1, dia, hora, minuto, segundo);

  return data.getFullYear() === ano && data.getMonth() === mes - 1 && data.getDate() === dia ? data : undefined;
}

function formatoSemHora(formato: string): string {
  const inicioHora = formato.split('').findIndex(caractere => TOKENS_HORA.includes(caractere));

  return inicioHora > 0 ? formato.substring(0, inicioHora).replace(/[\s\-:]+$/, '') : '';
}

function parseDataPadrao(texto: string, formato: string): Date | undefined {
  try {
    return flatpickr.parseDate(texto, formato);
  } catch {
    return undefined;
  }
}
